import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { useSelector } from 'react-redux';
import { selectUserToken } from '../../../../../redux/features/user/userSelectors';
import {
  streetsData,
  streetsDataError,
  streetsDataLoading,
} from '../../../../../redux/features/dashboard/dashboardSelectors';
import {
  useAddStreetNumberMutation,
  useDeleteStreetNumberMutation,
  useGetStreetNumberQuery,
  useUpdateStreetNumberMutation,
} from '../../../../../redux/services/data/streetsNumberApi';

import { FormTemplate } from '../../FormTemplate';
import { InputsTemplate } from '../../../inputs/InputsTemplate';
import { Loader } from '../../../../ui/Loader';
import { DeleteDialogButton } from '../../../DeleteDialogButton';

import {
  initialValues,
  mock,
  validationSchemaStreetNumber,
} from '../../../../../lib/mocks/add-street-number-mock';
import {
  getDefaultValues,
  handleResponseError,
} from '../../../../../utils/data';
import { closeDialog, showError } from '../../../../../utils/ui';
import {
  errorMessages,
  selectsLocationNames,
} from '../../../../../data/constants';

export const AddStreetNumberForm = ({
  formProps = {},
  name = {},
  isEdit = false,
  handleClose = () => {},
  handleResetInput = () => {},
}) => {
  const [error, setError] = useState('');

  const userToken = useSelector(selectUserToken);
  const streets = useSelector(streetsData);
  const streetsLoading = useSelector(streetsDataLoading);
  const streetsError = useSelector(streetsDataError);

  // Memoized values from parent form
  const streetNumberId = useMemo(
    () => formProps?.values?.[name],
    [formProps?.values, name]
  );
  const selectedStreetId = useMemo(
    () => formProps?.values?.[selectsLocationNames.street],
    [formProps?.values]
  );

  // RTK Query hooks
  const {
    data: streetNumber,
    isFetching: isStreetNumberLoading,
    error: streetNumberError,
  } = useGetStreetNumberQuery(
    { token: userToken, id: streetNumberId },
    { skip: !isEdit || !streetNumberId || !userToken }
  );
  const [addStreetNumber, { isLoading: isAddingLoading, error: addingError }] =
    useAddStreetNumberMutation();
  const [
    editStreetNumber,
    { isLoading: isEditingLoading, error: editingError },
  ] = useUpdateStreetNumberMutation();
  const [
    deleteStreetNumber,
    { isLoading: isDeletingLoading, error: deletingError },
  ] = useDeleteStreetNumberMutation();

  // Combined loading state
  const isLoading =
    streetsLoading ||
    (isEdit && isStreetNumberLoading) ||
    isAddingLoading ||
    isEditingLoading ||
    isDeletingLoading;

  // Find selected street object from streets array
  const selectedStreet = useMemo(() => {
    if (!selectedStreetId || !streets?.length) return null;
    return streets.find(
      s =>
        s.id.toString() === selectedStreetId.toString() ||
        s.value.toString() === selectedStreetId.toString()
    );
  }, [selectedStreetId, streets]);

  // Prepare mock config with streets data injected
  const mockWithData = useMemo(() => {
    if (!streets) return mock;
    return mock.map(item => {
      if (item.name === 'street_id') {
        return { ...item, selectData: streets };
      }
      return item;
    });
  }, [streets]);
  // Prepare form initial values
  const formInitialValues = useMemo(() => {
    if (isEdit && streetNumber) {
      // Edit mode: use fetched street number data
      return {
        name: streetNumber.name || '',
        latitude: streetNumber.latitude || '',
        longitude: streetNumber.longitude || '',
        street_id: streetNumber.info?.street_id.id || selectedStreetId || '',
        postal_code: streetNumber.postal_code || '',
      };
    }
    // Add mode: use selected street from parent form
    return {
      ...initialValues,
      street_id: selectedStreetId || '',
    };
  }, [isEdit, streetNumber, selectedStreetId]);

  // Prepare mock config with default values for form fields
  const mockWithValues = useMemo(() => {
    if (isEdit && streetNumber) {
      // For edit mode, set defaultValue for street select
      return getDefaultValues(
        {
          street_id: selectedStreet || streetNumber.info?.street_id,
        },
        mockWithData
      );
    } else if (!isEdit && selectedStreet) {
      // For add mode, set defaultValue for pre-selected street
      return getDefaultValues({ street_id: selectedStreet }, mockWithData);
    }
    return mockWithData;
  }, [isEdit, streetNumber, selectedStreet, mockWithData]);

  // Handle delete
  const handleDelete = async setIsDeleteClick => {
    setIsDeleteClick(false);
    setError('');

    const response = await deleteStreetNumber({
      token: userToken,
      id: streetNumberId,
    });

    const errorStatus = handleResponseError(
      response?.error,
      deletingError,
      () => closeDialog({ handleClose })
    );

    if (!errorStatus) {
      handleResetInput();
    }

    setError(errorStatus);
  };

  // Handle submit
  const handleSubmit = async values => {
    setError(null);

    let response;
    let errorStatus;

    if (isEdit) {
      response = await editStreetNumber({
        token: userToken,
        id: streetNumberId,
        data: values,
      });

      errorStatus = handleResponseError(response?.error, editingError, () =>
        closeDialog({ handleClose })
      );
    } else {
      response = await addStreetNumber({ token: userToken, data: values });

      errorStatus = handleResponseError(response?.error, addingError, () =>
        closeDialog({ handleClose })
      );
    }

    setError(errorStatus);
  };

  // Show errors
  useEffect(() => {
    if (streetsError || streetNumberError) {
      setError(errorMessages.wentWrong);
    }
  }, [streetsError, streetNumberError]);

  useEffect(() => {
    showError(error);
  }, [error]);

  return (
    <div className="relative flex flex-col gap-4 w-[260px] sm:w-[420px] exclude-click">
      {!isLoading && (
        <FormTemplate
          initialValues={formInitialValues}
          validationSchema={Yup.object(validationSchemaStreetNumber)}
          handleSubmitForm={handleSubmit}
          buttonText={'Save'}
          bgColor={'bg-purpleColor border-purpleColor'}
        >
          {formProps => (
            <>
              <InputsTemplate
                formProps={formProps}
                inputsList={mockWithValues}
              />

              {isEdit && (
                <DeleteDialogButton
                  isDeletingLoading={isDeletingLoading}
                  handleDelete={handleDelete}
                  error={error}
                />
              )}
            </>
          )}
        </FormTemplate>
      )}

      {isLoading && (
        <div className="flex items-center justify-center w-full h-[152px]">
          <Loader />
        </div>
      )}
    </div>
  );
};
